import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import { usePrevious } from "@utils/CustomHooks";
import { displayErrorMessage } from "@utils/DisplayMessageUtility";
import { ErrorParser } from "@utils/ErrorMessageUtils";
import { getLanguagesForView, getUserLanguages } from "@utils/UserUtils";

import { Language } from "@common/domain/models/Language";
import { fetchLanguages } from "@common/redux/common/actions";
import {
  getLangaugesSelector,
  getLanguagesErrorSelector,
  getLanguagesLoaderSelector,
} from "@common/redux/common/selectors";

import { translate } from "@translations/Translater";

import AnimatedSearchDropdown from "@components/atoms/Dropdown";
import ArrowDropDown from "@components/atoms/Dropdown/ArrorDropDown/index";
import Text, { FontFamilyVariant, TextType } from "@components/Text";

import { DEFAULT_LANG } from "@constants/config";

import { authMessages } from "@containers/Auth/message";

import "./styles.less";

interface IProps {
  fetchLanguages: () => void;
  languages: Language[];
  isFetchingLanguages: boolean;
  fetchLanguagesError: string;
  onSelectLanguage: (value: string) => void;
  preferredLangCode?: string;
  containerStyle?: string;
  labelClassName?: string;
  dropdownStyle?: string;
  disabled?: boolean;
  isAnimatedDropdown?: boolean;
}

const LanguageDropdown = (props: IProps) => {
  const { t } = useTranslation();
  const [langList, setLangList] = useState([]);
  const [language, setLanguage] = useState(DEFAULT_LANG);
  const {
    isFetchingLanguages,
    fetchLanguagesError,
    fetchLanguages,
    onSelectLanguage,
    languages,
    preferredLangCode,
    containerStyle,
    labelClassName,
    dropdownStyle,
    disabled = false,
    isAnimatedDropdown = false
  } = props;

  const previousIsFetchingLanguage = usePrevious({ isFetchingLanguages });

  useEffect(function onMount() {
    if (!languages.length) {
      fetchLanguages();
    }
    handleLanguage();
  }, []);

  useEffect(()=>{

     if (previousIsFetchingLanguage && !isFetchingLanguages) {
      if (!fetchLanguagesError) {
      handleLanguage();
    } else {
      displayErrorMessage(ErrorParser(fetchLanguagesError));
    }
  }
  },[fetchLanguagesError, isFetchingLanguages]);

  useEffect(() => {
        if(preferredLangCode){
          const prefferedLang = languages.find((lang) => lang.getLanguageCode() === preferredLangCode);
          if(prefferedLang){
           setLanguage(getLanguagesForView(prefferedLang.getName()));
          }
        }
  }, [preferredLangCode, languages]);

  const handleLanguage = () => {
    const languagesList =  isAnimatedDropdown ? languages.map((lang) => { return { key: lang.getName(), value: getLanguagesForView(lang.getName()) }}) :
                          languages.map((lang) => getLanguagesForView(lang.getName()));
    setLangList(languagesList);
  }

  const onLangChange = (itemValue: string) => {
    let languageCode = '';
    if (isAnimatedDropdown) {
      const selectedLang = langList?.find((item) => item?.value === itemValue);
      setLanguage(itemValue);
      languageCode = languages.find((lang) => lang.getName() === selectedLang?.key)?.getLanguageCode();
      onSelectLanguage(languageCode);
    }
    if (!isAnimatedDropdown) {
      const languageList = languages.map((lang) => { return { key: lang.getName(), value: getLanguagesForView(lang.getName()) } });
      const selectedLangData = languageList?.find((item) => item?.value === itemValue);
      setLanguage(itemValue);
      languageCode = languages.find((lang) => lang.getName() === selectedLangData?.key)?.getLanguageCode();
      onSelectLanguage(languageCode);
    }
  }

  return (
    <div id="language">
      {!isAnimatedDropdown ?
        <ArrowDropDown
          className={dropdownStyle}
          containerId="language"
          list={langList}
          onChange={onLangChange}
          value={language}
          disabled={disabled}
        /> :
        <AnimatedSearchDropdown
          data={langList}
          disabled={disabled}
          isEmptyList={false}
          label={translate(t, authMessages.language)}
          onChange={onLangChange}
          prefill={language}
        />
      }
    </div>
  );
};

export const mapStateToProps = (state: any) => ({
  isFetchingLanguages: getLanguagesLoaderSelector(state),
  fetchLanguagesError: getLanguagesErrorSelector(state),
  languages: getLangaugesSelector(state),
});

export const mapDispatchToProps = (dispatch: any) => ({
  fetchLanguages: () => dispatch(fetchLanguages()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LanguageDropdown);
